// Role resolution, shared by the auth flow, the router, the nav and the route guard.
// Depends on nothing but constants, so `config-global` can use it without an import cycle.
// The API applies the same rules — see backend/src/dal/user_organizations.

import { WEB_CALLING_ENABLED, WEB_CALLING_ENABLED_ORG_IDS } from 'src/constants/misc';

/**
 * Roles that only exist platform-wide. `user_organizations` can only say owner or volunteer,
 * so a membership row cannot express — and therefore must not override — these. Without this,
 * a legislative member holding a membership row would be quietly demoted (or promoted).
 */
export const GLOBAL_ONLY_ROLES = ['superadmin', 'legislative_member', 'national_viewer'];

/**
 * Roles that work across every organization with no organization context, the way superadmin
 * does. national_viewer is the read-only one; the API limits it to production organizations.
 */
export const CROSS_ORG_ROLES = ['superadmin', 'national_viewer'];

export const isCrossOrgRole = (role) => CROSS_ORG_ROLES.includes(role);

/**
 * Maps org-table role codes to their canonical equivalents used across the app.
 * user_roles table uses: manager, user
 * user_organizations table uses: owner, volunteer
 */
export const normalizeRole = (role) => {
  if (role === 'owner') return 'manager';
  if (role === 'volunteer') return 'user';
  return role;
};

/**
 * The role Hasura authorizes this session as: the `role` claim that
 * backend/src/services/auth/cognito-pre-token.ts writes onto the ID token. Unlike the effective
 * role it is fixed per sign-in, not per organization — `user` only for a global volunteer who
 * manages no organization; a manager who volunteers elsewhere is `manager` everywhere. Hasura
 * role `user` cannot select many tables `manager` can, so code shared with managers checks this
 * to skip queries Hasura would reject. `null` when there is no session to read.
 */
export const hasuraRoleOf = (user) => user?.signInUserSession?.idToken?.payload?.role ?? null;

/**
 * The role that governs what a user may see and do in the organization they've selected:
 * the per-org role wins, except for the platform-wide roles above. Idempotent, so it's safe
 * to pass either a raw org role code (owner/volunteer) or an already-normalized one.
 */
export const resolveEffectiveRole = (globalRole, orgRole) => {
  if (GLOBAL_ONLY_ROLES.includes(globalRole)) return globalRole;
  return normalizeRole(orgRole) || globalRole;
};

/**
 * Human label for a user's role, for lists and profiles that describe *another* user.
 *
 * Prefers their role in the organization being viewed and falls back to the global role when
 * there is no membership for it. The fallback is not an edge case: a superadmin's
 * OrganizationContext has no selected org (`selectedOrganizationId` is 0), so on the
 * superadmin's screens nothing ever matches and every label comes from the global role —
 * which is why `manager` has to be handled here and not just `superadmin`/`legislative_member`.
 */
export const roleDisplayLabel = (orgRole, globalRole) => {
  const role = normalizeRole(orgRole) || globalRole;
  if (role === 'superadmin') return 'Super Admin';
  if (role === 'legislative_member') return 'Legislative Member';
  if (role === 'manager') return 'Manager';
  return 'Volunteer';
};

// ----------------------------------------------------------------------
// Org memberships (rows of user_organizations)

/** `flg_inactive: 'Y'` is a switched-off membership: it grants nothing, anywhere. */
export const isActiveMembership = (membership) => membership?.flg_inactive !== 'Y';

/** An active membership that makes the user a manager of that org. */
export const isManagerMembership = (membership) =>
  isActiveMembership(membership) &&
  normalizeRole(membership?.user_organizations_role?.code) === 'manager';

/**
 * The memberships an account may act on in the web app.
 *
 * Web used to be the manager surface only — volunteering was the mobile app's job — so this
 * filtered to manager rows and a volunteer ended up with no selectable organization at all.
 * Assigned Tasks (docs/superpowers/specs/2026-09-04-web-assigned-tasks-calling-design.md)
 * makes web a volunteer surface too, so while WEB_CALLING_ENABLED is on an active volunteer
 * membership counts as well. With it off, this is the manager-only rule again.
 *
 * Manager rows sort first because findGoverningMembership takes [0]: a user holding both a
 * volunteer and an owner row in one org must resolve to manager, not be confined by
 * EffectiveRoleGuard to the volunteer screens.
 *
 * Platform-wide roles are unchanged either way — the org table can't express
 * legislative_member or superadmin, so filtering them by an org role code would leave them with
 * no orgs at all.
 *
 * `includeVolunteers` exists for tests; callers take the flag's value.
 */
export const webEligibleMemberships = (
  globalRole,
  memberships = [],
  includeVolunteers = WEB_CALLING_ENABLED
) => {
  const active = memberships.filter(isActiveMembership);
  if (GLOBAL_ONLY_ROLES.includes(globalRole)) return active;
  const managed = active.filter(isManagerMembership);
  if (!includeVolunteers) return managed;
  return [...managed, ...active.filter((m) => !isManagerMembership(m))];
};

/**
 * An active volunteer membership in an organization listed in WEB_CALLING_ENABLED_ORG_IDS — the
 * only kind that lets someone who manages nothing into the web portal. The id is coerced because
 * the list holds numbers and rows do not promise a type.
 */
const isCallingVolunteerMembership = (membership) =>
  isActiveMembership(membership) &&
  !isManagerMembership(membership) &&
  WEB_CALLING_ENABLED_ORG_IDS.includes(
    Number(membership?.user_organizations_organization?.organization_id)
  );

/**
 * Whether this account belongs in the mobile app rather than the web portal: it holds an active
 * membership, manages no organization, and has no volunteer membership that web calling is on
 * for — with WEB_CALLING_ENABLED off that is anyone who only volunteers; with it on, anyone who
 * only volunteers in organizations missing from WEB_CALLING_ENABLED_ORG_IDS (one listed org is
 * enough to let them in). Such a user is signed in and sent to the app-download page
 * (paths.volunteers.root) instead of being refused. Managers are unaffected, as are the
 * platform-wide roles.
 *
 * `includeVolunteers` exists for tests; callers take the flag's value.
 */
export const isAppOnlyUser = (
  globalRole,
  memberships = [],
  includeVolunteers = WEB_CALLING_ENABLED
) =>
  !GLOBAL_ONLY_ROLES.includes(globalRole) &&
  memberships.some(isActiveMembership) &&
  !memberships.some(isManagerMembership) &&
  !(includeVolunteers && memberships.some(isCallingVolunteerMembership));
